import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CalendarCheck, Mail, Mails, AlertCircle, ChevronDown, ChevronRight, Calendar,
  Check, RotateCcw, MapPin, Phone, FileText, Globe,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { api, apiErrorMessage } from "@/lib/api";
import { RESERVA_TYPE_LABELS } from "@/pages/trips/shared";
import TripEmailComposer from "@/pages/trips/TripEmailComposer";
import { ConnectEmailDialog } from "@/pages/trips/AsistenteTab";
import { UserChip } from "@/components/UserAvatar";
import { TYPE_ICON, ESTADO_META, CHANNEL_META, fmtDayHeader, fmtShortDate, GROUP_TYPE_LABELS, paxLabel } from "@/pages/trips/reconfirmShared";

const CHANNEL_ICON = { mail: Mail, telefono: Phone, whatsapp: Phone, voucher: FileText, web: Globe };

export default function ReconfirmacionesTab({ tripId }) {
  const qc = useQueryClient();
  const [showConnect, setShowConnect] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [collapsedDays, setCollapsedDays] = useState(new Set());
  const [composer, setComposer] = useState(null);

  const dataQ = useQuery({ queryKey: ["recon-trip", tripId], queryFn: async () => (await api.get(`/reconfirmaciones/trip/${tripId}`)).data });
  const data = dataQ.data;
  const reservas = useMemo(() => data?.reservas || [], [data]);
  const emailConnected = !!data?.email_connected;

  const days = useMemo(() => {
    const groups = {};
    reservas.forEach((r) => { const k = r.date_entry || ""; (groups[k] = groups[k] || []).push(r); });
    return Object.entries(groups).sort(([a], [b]) => (a || "9999").localeCompare(b || "9999"));
  }, [reservas]);

  const estadoMut = useMutation({
    mutationFn: async ({ rid, estado }) => (await api.patch(`/reconfirmaciones/reserva/${rid}`, { estado })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["recon-trip", tripId] }); qc.invalidateQueries({ queryKey: ["trip", tripId] }); qc.invalidateQueries({ queryKey: ["recon-daily"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const toggleSelect = (r) => setSelected((p) => { const n = new Set(p); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; });
  const toggleDay = (k) => setCollapsedDays((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const selectedReservas = reservas.filter((r) => selected.has(r.id));
  const selectedProviderIds = new Set(selectedReservas.map((r) => r.provider_id).filter(Boolean));

  const onSendMulti = () => {
    if (selectedProviderIds.size !== 1) { toast.error("Las reservas seleccionadas deben ser del mismo proveedor"); return; }
    const first = selectedReservas[0];
    setComposer({ multi: true, reservaIds: selectedReservas.map((r) => r.id), providerId: first.provider_id, toEmail: first.provider_email, providerName: first.provider_name });
  };

  const onCloseComposer = (refresh) => {
    setComposer(null);
    if (refresh) {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["recon-trip", tripId] });
      qc.invalidateQueries({ queryKey: ["trip", tripId] });
    }
  };

  if (dataQ.isLoading) return <div className="space-y-3"><Skeleton className="h-12 w-full" /><Skeleton className="h-72 w-full" /></div>;

  return (
    <div className="space-y-4" data-testid="reconfirmaciones-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Operativa · Soporte</p>
          <h2 className="flex items-center gap-2 font-heading text-xl font-semibold"><CalendarCheck className="h-5 w-5 text-primary" /> Reconfirmaciones</h2>
          <p className="text-sm text-muted-foreground">Reconfirma los servicios del viaje. Los de canal <strong>Mail</strong> se reconfirman por correo con la plantilla.</p>
        </div>
        {emailConnected ? (
          <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm" data-testid="recon-email-connected">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> Enviando como <span className="font-medium">{data.sender_email}</span>
            <Button variant="ghost" size="sm" className="ml-1 h-7" onClick={() => setShowConnect(true)} data-testid="recon-change-email">Cambiar</Button>
          </div>
        ) : (
          <Button onClick={() => setShowConnect(true)} data-testid="recon-connect-email"><Mail className="mr-1.5 h-4 w-4" />Conectar mi correo</Button>
        )}
      </div>

      {!emailConnected && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="recon-email-warning">
          <AlertCircle className="h-4 w-4" /> Conecta tu correo para enviar las reconfirmaciones por email.
        </div>
      )}

      {selected.size > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2 shadow-sm" data-testid="recon-selection-bar">
          <span className="text-sm font-medium">{selected.size} servicio(s) seleccionado(s)</span>
          {selectedProviderIds.size > 1 && <span className="text-xs text-red-600">· proveedores distintos</span>}
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}>Limpiar</Button>
            <Button size="sm" onClick={onSendMulti} disabled={!emailConnected || selectedProviderIds.size !== 1} data-testid="recon-send-multi">
              <Mails className="mr-1.5 h-4 w-4" />Reconfirmar en un correo ({selected.size})
            </Button>
          </div>
        </div>
      )}

      {reservas.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="recon-empty">Este viaje no tiene servicios que reconfirmar.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {days.map(([dayKey, rows]) => {
            const collapsed = collapsedDays.has(dayKey);
            return (
              <Card key={dayKey || "nofecha"} className="overflow-hidden" data-testid={`recon-day-${dayKey || "nofecha"}`}>
                <button onClick={() => toggleDay(dayKey)} className="flex w-full items-center gap-2 border-b bg-muted/40 px-4 py-2.5 text-left hover:bg-muted/70">
                  {collapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold">{fmtDayHeader(dayKey)}</span>
                  <span className="text-xs text-muted-foreground">· {rows.length} servicio(s)</span>
                </button>
                {!collapsed && (
                  <ReconDaySubgroups rows={rows} rowProps={(r) => ({
                    selected: selected.has(r.id), onToggle: () => toggleSelect(r),
                    emailConnected, estadoMut,
                    onSend: () => setComposer({ multi: false, reservaIds: [r.id], providerId: r.provider_id, toEmail: r.provider_email, providerName: r.provider_name }),
                  })} />
                )}
              </Card>
            );
          })}
        </div>
      )}

      {composer && (
        <TripEmailComposer open={!!composer} onClose={onCloseComposer} tripId={tripId}
          providerId={composer.providerId} reservaIds={composer.reservaIds} toEmail={composer.toEmail}
          providerName={composer.providerName} multi={composer.multi} emailConnected={emailConnected}
          senderEmail={data?.sender_email} apiBase="/reconfirmaciones" titleSingle="Reconfirmar servicio" />
      )}

      <ConnectEmailDialog open={showConnect} onClose={() => setShowConnect(false)} currentEmail={data?.sender_email}
        onConnected={() => qc.invalidateQueries({ queryKey: ["recon-trip", tripId] })} />
    </div>
  );
}

export function ReconRow({ r, selected, onToggle, emailConnected, estadoMut, onSend, showClient = false, onOpenReserva, onOpenTrip }) {
  const Icon = TYPE_ICON[r.type] || MapPin;
  const ChIcon = CHANNEL_ICON[r.channel] || Globe;
  const est = ESTADO_META[r.estado] || ESTADO_META.pendiente;
  const ch = CHANNEL_META[r.channel] || CHANNEL_META.desconocido;
  const reconfirmed = r.estado === "reconfirmado";
  const booked = r.booked !== false;
  return (
    <div className="flex flex-wrap items-start gap-2 px-4 py-2.5 sm:flex-nowrap sm:gap-3" data-testid={`recon-row-${r.id}`}>
      <Checkbox className="mt-0.5" checked={selected} disabled={!r.mail_ready} onCheckedChange={onToggle} data-testid={`recon-select-${r.id}`} />
      <span className="w-12 shrink-0 font-mono text-xs text-muted-foreground">{r.hour_entry || "—"}</span>
      <span className="flex w-24 shrink-0 items-center gap-1 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {RESERVA_TYPE_LABELS[r.type] || r.type}</span>
      <div className="min-w-[160px] flex-1">
        {showClient && (onOpenTrip
          ? <button onClick={onOpenTrip} className="block max-w-full truncate text-left text-[11px] font-medium text-primary hover:underline" data-testid={`recon-open-trip-${r.id}`}>{r.client_name}</button>
          : <div className="truncate text-[11px] font-medium text-primary">{r.client_name}</div>)}
        {onOpenReserva
          ? <button onClick={onOpenReserva} className="block max-w-full truncate text-left text-sm font-medium hover:text-primary hover:underline" title={r.service} data-testid={`recon-open-reserva-${r.id}`}>{r.service || "—"}</button>
          : <div className="truncate text-sm font-medium" title={r.service}>{r.service || "—"}</div>}
        <div className="truncate text-xs text-muted-foreground">{r.provider_name || <span className="text-red-600">sin proveedor</span>}{r.city ? ` · ${r.city}` : ""}</div>
        <ReconDetails r={r} />
        {!booked && (
          <div className="mt-1 inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800" data-testid={`recon-pending-booking-${r.id}`}>
            <AlertCircle className="h-3 w-3" /> Servicio pendiente de reservar · márcalo como Reservado (R) en Reservas
          </div>
        )}
        {r.reconfirmed_by ? (
          <div className="mt-0.5 flex items-center gap-1 text-[10px] text-emerald-700" data-testid={`recon-by-${r.id}`}>Reconfirmado por <UserChip name={r.reconfirmed_by.name} color={r.reconfirmed_by.color} /></div>
        ) : r.requested_by ? (
          <div className="mt-0.5 flex items-center gap-1 text-[10px] text-amber-700" data-testid={`recon-by-${r.id}`}>Solicitada por <UserChip name={r.requested_by.name} color={r.requested_by.color} /></div>
        ) : null}
      </div>
      <Badge variant="outline" className={`shrink-0 gap-1 text-[10px] ${ch.cls}`} title={r.provider_confirmacion}><ChIcon className="h-3 w-3" />{ch.label}</Badge>
      <Badge variant="outline" className={`shrink-0 text-[10px] ${est.cls}`} data-testid={`recon-estado-${r.id}`}>{est.label}</Badge>
      <div className="flex shrink-0 items-center gap-1.5">
        {booked && r.mail_ready && !reconfirmed && (
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!emailConnected} onClick={onSend} data-testid={`recon-send-${r.id}`}>
            <Mail className="mr-1 h-3 w-3" />Reconfirmar
          </Button>
        )}
        {reconfirmed ? (
          <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => estadoMut.mutate({ rid: r.id, estado: "pendiente" })} data-testid={`recon-reopen-${r.id}`}>
            <RotateCcw className="mr-1 h-3 w-3" />Reabrir
          </Button>
        ) : booked ? (
          <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={() => estadoMut.mutate({ rid: r.id, estado: "reconfirmado" })} data-testid={`recon-mark-${r.id}`}>
            <Check className="mr-1 h-3 w-3" />Marcar (F)
          </Button>
        ) : (
          <Button size="sm" variant="secondary" className="h-7 text-xs" disabled title="Servicio pendiente de reservar" data-testid={`recon-mark-disabled-${r.id}`}>
            <Check className="mr-1 h-3 w-3" />Marcar (F)
          </Button>
        )}
      </div>
    </div>
  );
}

// Datos operativos del servicio (hotel vs resto), siempre visibles bajo el nombre.
function ReconDetails({ r }) {
  const Item = ({ label, value, warn, testid }) => (
    <span className={`inline-flex items-baseline gap-1 ${warn ? "text-red-600" : ""}`} data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground/80">{label}</span>
      <span className="font-medium">{value}</span>
    </span>
  );
  const items = [];
  if (r.type === "alojamiento") {
    if (r.date_entry) items.push(<Item key="ci" label="Check-in" value={fmtShortDate(r.date_entry)} />);
    if (r.date_exit) items.push(<Item key="co" label="Check-out" value={fmtShortDate(r.date_exit)} />);
    if (r.nights) items.push(<Item key="n" label="Noches" value={r.nights} />);
    const room = [r.room, r.board_label].filter(Boolean).join(" · ");
    items.push(<Item key="room" label="Habitación" value={room || "—"} warn={!room} />);
    items.push(<Item key="pax" label="Pax" value={paxLabel(r)} />);
  } else {
    if (r.group_type) items.push(<Item key="gt" label="Modalidad" value={GROUP_TYPE_LABELS[r.group_type] || r.group_type} />);
    items.push(<Item key="pax" label="Pax" value={paxLabel(r)} />);
    items.push(<Item key="h" label="Hora" value={r.hour_entry || "—"} warn={!r.hour_entry} />);
    items.push(<Item key="d" label="Duración" value={r.td_duration || "—"} warn={!r.td_duration} />);
    if (r.td_guide_name || r.guide_missing) items.push(<Item key="g" label="Guía" value={r.td_guide_name || "falta nombre del guía/driver"} warn={!r.td_guide_name} testid={`recon-guide-${r.id}`} />);
    items.push(<Item key="p" label="Contact phone" value={r.td_contact_phone || "—"} warn={!r.td_contact_phone} />);
  }
  return (
    <div className="mt-1 space-y-0.5 text-xs" data-testid={`recon-details-${r.id}`}>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">{items}</div>
      {r.note && <div className="line-clamp-2 italic text-muted-foreground" title={r.note} data-testid={`recon-note-${r.id}`}>Notas: {r.note}</div>}
    </div>
  );
}

// Subdivide las filas de un día en "Sin reconfirmar" y "Reconfirmadas".
export function ReconDaySubgroups({ rows, rowProps }) {
  const no = rows.filter((r) => r.estado !== "reconfirmado");
  const yes = rows.filter((r) => r.estado === "reconfirmado");
  return (
    <div data-testid="recon-day-subgroups">
      {no.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 border-b bg-amber-50/60 px-4 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700" data-testid="recon-subgroup-pendientes">Sin reconfirmar · {no.length}</div>
          <div className="divide-y">{no.map((r) => <ReconRow key={r.id} r={r} {...rowProps(r)} />)}</div>
        </div>
      )}
      {yes.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 border-b border-t bg-emerald-50/60 px-4 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700" data-testid="recon-subgroup-reconfirmadas">Reconfirmadas · {yes.length}</div>
          <div className="divide-y">{yes.map((r) => <ReconRow key={r.id} r={r} {...rowProps(r)} />)}</div>
        </div>
      )}
    </div>
  );
}
